// Status mapping from PipelineView - maps French statuses to stage IDs
export const normalizeText = (text: string | undefined): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .trim();
};

export const getStageFromStatus = (status: string | undefined): number => {
  if (!status) return 110; // Default to completed
  
  const statusNormalized = normalizeText(status);
  
  // Complete status mapping from PipelineView including all variations
  const statusMapping: Record<string, number> = {
    /* 10 – dossier tout juste créé, rien n'est planifié */
    'bureau à encoder': 10,
    'bureau a encoder': 10,

    /* 20 – préparation / prise de rendez-vous */
    'bureau à planifier': 20,
    'bureau a planifier': 20,

    /* 30 – rv en attente de planification */
    'rv en attente*': 30,

    /* 40 – visite terrain à organiser ou en cours */
    'expert à visiter': 40,
    'expert a visiter': 40,

    /* 50 – rapport d'expertise à boucler */
    'expert à achever': 50,
    'expert a achever': 50,

    /* 60 – rapport d'expertise à valoriser */
    'bureau à valoriser': 60,
    'bureau a valoriser': 60,

    /* 70 – rapport d'expertise à achever */
    'bureau à achever': 70,
    'bureau a achever': 70,
    '*à achever': 70,
    '*a achever': 70,

    /* 80 – attentes administratives : docs, paiements, relances */
    'attente doc/infos': 80,
    'attente documents/infos': 80,

    /* 90 – attentes administratives : paiements, relances */
    'attente paiement*': 90,
    'attente de paiement + conf': 90,
    'demande annulee + att paiement client': 90,
    'attente de facturation': 90,
    '*rappel paiement client': 90,
    '1er rappel paiement courtier': 90,
    '2eme rappel paiement courtier': 90,
    '3eme rappel paiement courtier sms': 90,
    '3eme rappel tf + mail (menace solvency)': 90,
    '*rappel paiement courtier*': 90,
    'attente de facturation mensuelle': 90,
    'solvency': 90,

    /* 100 – validations externes ou mise en attente contrôlée */
    'attente confirmation*': 100,
    'facture en attente confirmation': 100,
    'mandat de vente en cours': 100,

    /* 110 – prêt à être envoyé / transmis */
    'à transmettre': 110,
    'a transmettre': 110,
  };
  
  // Exact matches first (both normalized)
  for (const [key, stageId] of Object.entries(statusMapping)) {
    const keyNormalized = normalizeText(key);
    if (keyNormalized === statusNormalized) {
      return stageId;
    }
  }
  
  // Wildcard matches - prefix and suffix (same logic as PipelineView)
  for (const [key, stageId] of Object.entries(statusMapping)) {
    const keyNormalized = normalizeText(key);
    
    // Handle wildcard prefix (starts with *)
    if (keyNormalized.startsWith('*')) {
      const pattern = keyNormalized.substring(1); // Remove *
      if (statusNormalized.endsWith(pattern)) {
        return stageId;
      }
    }
    
    // Handle wildcard suffix (ends with *)
    if (keyNormalized.endsWith('*')) {
      const pattern = keyNormalized.substring(0, keyNormalized.length - 1); // Remove *
      if (statusNormalized.startsWith(pattern)) {
        return stageId;
      }
    }
  }
  
  // Default to stage 110 (100% complete) if no match - assumes completed/archived statuses
  console.warn(`Status "${status}" not found in mapping, defaulting to stage 110 (100% complete)`);
  return 110;
};

// Get progress percentage from stage ID
export const getProgressFromStage = (stageId: number): number => {
  const stageProgressMap: Record<number, number> = {
    10: 9,   // Bureau à encoder
    20: 18,  // Bureau à planifier
    30: 27,  // RV en attente
    40: 36,  // Expert à visiter
    50: 45,  // Expert à achever
    60: 54,  // Bureau à valoriser
    70: 64,  // Bureau à achever
    80: 73,  // Attente doc/infos
    90: 82,  // Attente paiement
    100: 91, // Attente confirmation
    110: 100 // À transmettre
  };
  
  return stageProgressMap[stageId] || 100;
};